import { Space, Button, Col, Row, Statistic, Drawer, Tabs, Flex, Tag, Typography, Divider, DatePicker } from 'antd';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { formatDate } from 'utils/date';
import { formatCurrency } from 'utils/number';
import Debt from '../Debt';
import CustomerInfoEdit from 'components/customer/CustomerInfoEdit';
import CustomerLoginEdit from 'components/customer/CustomerLoginEdit';
import ManualAdjustModal from './ManualAdjustModal';
import _ from 'lodash';

const { Text } = Typography;

const getOrderStatusTag = (status) => {
  if (status === 'canceled') return <Tag color="error">Đã huỷ</Tag>;
  if (status === 'finalized') return <Tag color="success">Đã hoàn tất</Tag>;
  return <Tag color="processing">Đang xử lý</Tag>;
};

const MobileInfoModal = ({
  isModalOpen,
  customer,
  orders,
  loyaltyEnabled,
  loyaltyByOrder,
  manualAdjustments,
  reload,
  showCustomerEdit,
  setShowCustomerEdit,
  showCustomerLoginEdit,
  setShowCustomerLoginEdit,
  isAccountLoginMode,
  handleCancel,
  onCustomerSave,
}) => {
  const navigate = useNavigate();
  const [pointFromDate, setPointFromDate] = useState(null);
  const [showAdjustModal, setShowAdjustModal] = useState(false);

  const infoTab = (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {/* Basic info */}
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        <Flex justify="space-between">
          <Text type="secondary">Họ và tên</Text>
          <Text strong>{customer.full_name}</Text>
        </Flex>
        <Divider style={{ margin: 0 }} />
        <Flex justify="space-between">
          <Text type="secondary">Số điện thoại</Text>
          <Text>{customer.phone}</Text>
        </Flex>
        <Divider style={{ margin: 0 }} />
        <Flex justify="space-between" align="flex-start" gap={8}>
          <Text type="secondary" style={{ flexShrink: 0 }}>Địa chỉ</Text>
          <Text style={{ textAlign: 'right' }}>{customer.address || '—'}</Text>
        </Flex>
      </Space>

      {/* Stats */}
      <Row gutter={[8, 8]}>
        <Col span={12}><Statistic title="Đơn hàng" value={orders.length} /></Col>
        <Col span={12}>
          <Statistic
            title="Tổng tiền"
            value={_.sumBy(orders, (x) => x.total_amount ?? 0)}
            precision={0}
          />
        </Col>
        <Col span={12}>
          <Statistic title="Xu" value={customer.loyalty_point?.current ?? 0} precision={0} />
        </Col>
        {customer.debt?.current_debt !== 0 && (
          <Col span={12}>
            <Statistic title="Nợ" value={customer.debt?.current_debt ?? 0} precision={0} />
          </Col>
        )}
      </Row>

      {/* Action buttons */}
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Button block type="primary" onClick={() => setShowCustomerEdit(true)}>
          Cập nhật thông tin
        </Button>
        {isAccountLoginMode && (
          <Button block onClick={() => setShowCustomerLoginEdit(true)}>
            Quản lý đăng nhập
          </Button>
        )}
      </Space>
    </Space>
  );

  const orderTab = (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      {(orders || []).map((record) => (
        <div
          key={record.id}
          style={{
            borderRadius: 8,
            border: '1px solid #f0f0f0',
            padding: '8px 12px',
            background: '#fafafa',
            cursor: 'pointer',
          }}
          onClick={() => navigate(`/order/${record.id}`)}
        >
          <Flex justify="space-between" align="center">
            <Space direction="vertical" size={0}>
              <Text strong style={{ fontSize: 13 }}>#{record.id}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>{formatDate(record.create_on)}</Text>
            </Space>
            {getOrderStatusTag(record.status)}
          </Flex>
          <Flex justify="space-between" style={{ marginTop: 6 }}>
            <Text style={{ fontSize: 13 }}>{record.total_item} sản phẩm</Text>
            <Text strong style={{ fontSize: 13 }}>{formatCurrency(record.total_amount)}</Text>
          </Flex>
        </div>
      ))}
      {(!orders || orders.length === 0) && (
        <Text type="secondary">Chưa có đơn hàng nào</Text>
      )}
    </Space>
  );

  const pointHistoryRows = useMemo(() => {
    const orderRows = (orders || []).map((o) => {
      const lp = loyaltyByOrder?.[o.id] || { earn: 0, use: 0 };
      return {
        rowKey: `order_${o.id}`,
        kind: 'order',
        id: o.id,
        create_on: o.create_on,
        sub_amount: o.sub_amount,
        total_amount: o.total_amount,
        earn: lp.earn || 0,
        use: lp.use || 0,
      };
    });
    const manualRows = (manualAdjustments || []).map((m) => ({
      rowKey: `manual_${m.id}`,
      kind: 'manual',
      id: m.id,
      create_on: m.create_on,
      reason: m.reason,
      earn: m.earn || 0,
      use: m.use || 0,
    }));
    let list = _.orderBy([...orderRows, ...manualRows], ['create_on'], ['desc']);
    if (!pointFromDate) return list;
    const fromTs = pointFromDate.startOf('day').valueOf();
    return list.filter((o) => o.create_on && new Date(o.create_on).getTime() >= fromTs);
  }, [orders, loyaltyByOrder, manualAdjustments, pointFromDate]);

  const totalEarn = _.sumBy(pointHistoryRows, (r) => r.earn || 0);
  const totalUse = _.sumBy(pointHistoryRows, (r) => r.use || 0);

  const pointHistoryTab = (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Flex align="center" gap={8}>
        <Text type="secondary" style={{ flexShrink: 0 }}>Từ ngày</Text>
        <DatePicker
          value={pointFromDate}
          onChange={(d) => setPointFromDate(d)}
          format="DD/MM/YYYY"
          allowClear
          style={{ width: '100%' }}
        />
      </Flex>
      <Button block type="primary" onClick={() => setShowAdjustModal(true)}>
        Điều chỉnh xu
      </Button>
      {pointHistoryRows.map((record) => {
        const isManual = record.kind === 'manual';
        return (
          <div
            key={record.rowKey}
            style={{
              borderRadius: 8,
              border: '1px solid #f0f0f0',
              padding: '8px 12px',
              background: '#fafafa',
              cursor: isManual ? 'default' : 'pointer',
            }}
            onClick={isManual ? undefined : () => navigate(`/order/${record.id}`)}
          >
            <Flex justify="space-between" align="center">
              <Space direction="vertical" size={0}>
                <Text strong style={{ fontSize: 13 }}>
                  {isManual ? 'Điều chỉnh tay' : `#${record.id}`}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>{formatDate(record.create_on)}</Text>
              </Space>
              {!isManual && (
                <Text strong style={{ fontSize: 13 }}>{formatCurrency(record.sub_amount ?? record.total_amount ?? 0)}</Text>
              )}
            </Flex>
            {isManual && record.reason && (
              <Text type="secondary" italic style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                {record.reason}
              </Text>
            )}
            <Flex justify="space-between" style={{ marginTop: 6 }}>
              <Text style={{ fontSize: 12 }} type="success">Tích: +{formatCurrency(record.earn || 0)} xu</Text>
              <Text style={{ fontSize: 12 }} type="danger">Sử dụng: -{formatCurrency(record.use || 0)} xu</Text>
            </Flex>
          </div>
        );
      })}
      {pointHistoryRows.length === 0 && (
        <Text type="secondary">Không có dữ liệu</Text>
      )}
      <Divider style={{ margin: '8px 0' }} />
      <Flex justify="space-between">
        <Text strong>Tổng tích: +{formatCurrency(totalEarn)} xu</Text>
        <Text strong>Tổng sử dụng: -{formatCurrency(totalUse)} xu</Text>
      </Flex>
    </Space>
  );

  const items = [
    {
      key: 'info',
      label: 'Thông tin',
      children: infoTab,
    },
    {
      key: 'order',
      label: 'Đơn hàng',
      children: orderTab,
    },
    {
      key: 'debt',
      label: 'Công nợ',
      children: <Debt customer={customer} onChange={onCustomerSave} />,
    },
  ];

  if (loyaltyEnabled) {
    items.push({
      key: 'loyalty',
      label: 'Lịch sử tích điểm',
      children: pointHistoryTab,
    });
  }

  return (
    <>
      <Drawer
        title="Thông tin khách hàng"
        placement="bottom"
        height="90%"
        open={isModalOpen}
        onClose={handleCancel}
        destroyOnClose
      >
        <Tabs items={items} />
      </Drawer>

      <CustomerInfoEdit
        id={customer.id}
        open={showCustomerEdit}
        onSave={onCustomerSave}
        onClose={() => setShowCustomerEdit(false)}
      />
      <CustomerLoginEdit
        id={customer.id}
        open={showCustomerLoginEdit}
        onSave={onCustomerSave}
        onClose={() => setShowCustomerLoginEdit(false)}
      />
      <ManualAdjustModal
        open={showAdjustModal}
        customer={customer}
        onClose={() => setShowAdjustModal(false)}
        onAdjusted={reload}
      />
    </>
  );
};

export default MobileInfoModal;
